/**
 * Creates a structured set of interactive color variables for different contexts (base, text, and optionally alpha levels).
 *
 * @template T - A string type that represents the base key for the interactive color variables.
 * @param key - The base key used to construct the CSS variable names.
 * @param alpha - A boolean flag to include optional alpha level variables (alpha20, alpha50, alpha80).
 * @returns A frozen object of type `InteractiveColorsVar<T>` containing CSS variable names for different contexts.
 *
 * ### Example
 * ```typescript
 * const colorVars = createInteractiveColorVars('primary', true);
 * // Result:
 * // {
 * //   base: {
 * //     default: 'var(--primary-base-default)',
 * //     action: 'var(--primary-base-action)',
 * //     active: 'var(--primary-base-active)',
 * //     activeAction: 'var(--primary-base-active-action)',
 * //   },
 * //   text: {
 * //     default: 'var(--primary-text-default)',
 * //     action: 'var(--primary-text-action)',
 * //     active: 'var(--primary-text-active)',
 * //     activeAction: 'var(--primary-text-active-action)',
 * //   },
 * //   alpha: {
 * //     alpha20: 'var(--primary-alpha-20)',
 * //     alpha50: 'var(--primary-alpha-50)',
 * //     alpha80: 'var(--primary-alpha-80)',
 * //   },
 * // }
 * ```
 *
 * ### Behavior
 * - **Base Variables:** Always included (`base` contains `default`, `action`, `active`, and `activeAction`).
 * - **Text Variables:** Always included (`text` contains `default`, `action`, `active`, and `activeAction`).
 * - **Alpha Variables:** Only included if the `alpha` parameter is `true`.
 * - The returned object is frozen using `Object.freeze()` to ensure immutability.
 *
 * @throws An error if the `key` is not provided, as it's required for variable generation.
 */
const createInteractiveColorVars = (key, alpha) => Object.freeze({
    base: {
        default: `var(--${key}-base-default)`,
        action: `var(--${key}-base-action)`,
        active: `var(--${key}-base-active)`,
        activeAction: `var(--${key}-base-active-action)`,
    },
    text: {
        default: `var(--${key}-text-default)`,
        action: `var(--${key}-text-action)`,
        active: `var(--${key}-text-active)`,
        activeAction: `var(--${key}-text-active-action)`,
    },
    ...(alpha
        ? {
            alpha: {
                alpha20: `var(--${key}-alpha-20)`,
                alpha50: `var(--${key}-alpha-50)`,
                alpha80: `var(--${key}-alpha-80)`,
            },
        }
        : {}),
});

export { createInteractiveColorVars };
